import React, { type JSX, useCallback } from 'react';

import { Box, Flex, Heading, Text } from '@rover/kibble/core';
import { isProdNodeEnv } from '@rover/rsdk/src/modules/Env/node';
import { Logger } from '@rover/rsdk/src/modules/Logging';
import type { SvgIcon } from '@rover/types/src/SvgIcon';

import {
  getIsTextSized,
  IconSizeMap,
  LinkColorMap,
  LinkCommonProps,
  LinkHoverColorMap,
  TypographyComponentProps,
} from './Link.common';

export type Props = LinkCommonProps;

type ExtendedIconProps = React.ComponentProps<SvgIcon> & {
  flexShrink?: string;
  marginLeft?: string;
  marginRight?: string;
};

export const LinkIcon: React.FC<ExtendedIconProps & { icon: SvgIcon }> = ({
  icon: Icon,
  // the destructuring of these props prevents errors from them being passed to an <svg />
  flexShrink, // eslint-disable-line @typescript-eslint/no-unused-vars
  marginLeft, // eslint-disable-line @typescript-eslint/no-unused-vars
  marginRight, // eslint-disable-line @typescript-eslint/no-unused-vars
  ...other
}) => {
  return <Icon {...other} fill="currentColor" />;
};

const getTypographyComponentProps = ({
  fontWeight,
  underline,
  asInlineLink,
  variant,
}: {
  fontWeight: Props['fontWeight'];
  underline: Props['underline'];
  asInlineLink?: true;
  variant: NonNullable<Props['variant']>;
}): TypographyComponentProps<'sx'> => {
  return {
    // this spread+ternary avoids passing fontWeight: undefined which breaks default fontWeights on the typog components
    ...(fontWeight ? { fontWeight } : undefined),
    textDecoration: underline ? 'underline' : 'none',
    sx: {
      display: asInlineLink ? 'inline' : 'inline-flex',
      alignItems: 'center',
      color: LinkColorMap[variant],
      '&:hover': {
        color: LinkHoverColorMap[variant],
        textDecoration: 'underline',
      },
    },
  };
};

// rel and windowFeatures are separate concepts, but noopener/noreferrer are
// valid and necessary in both, so this maps rel's value to the windowFeatures string.
const REL_TO_WINDOW_FEATURES: Partial<Record<NonNullable<Props['rel']>, string>> = {
  noopener: 'noopener',
  noreferrer: 'noreferrer',
  'noopener noreferrer': 'noopener,noreferrer',
};

/** Takes the reader to another page, a section of the same page, or a site outside Rover. */
function Link({
  children,
  icon,
  iconAlignment = 'left',
  asInlineLink,
  size = '200',
  testID,
  underline = true,
  variant = 'primary',
  fontWeight,
  webProps = {},
  onPress,
  href: topLevelHref,
  rel: topLevelRel,
  target: topLevelTarget,

  // pull out rxn-native-specific props and do nothing with them
  rxnProps, // eslint-disable-line @typescript-eslint/no-unused-vars
  ...other
}: Props): JSX.Element {
  const {
    as,
    href: webPropsHref,
    target: webPropsTarget,
    rel: webPropsRel,
    sx,
    ...otherWebProps
  } = webProps;

  // webProps overrides top-level props
  const href = webPropsHref ?? topLevelHref;
  const target = webPropsTarget ?? topLevelTarget ?? '_self';
  const rel = webPropsRel ?? topLevelRel;

  if (!children && !other['aria-label'] && !other['aria-labelledby']) {
    const error = new Error('Link.props MUST have children, or a11y values');
    if (isProdNodeEnv()) {
      Logger.error(error);
    } else {
      // eslint-disable-next-line no-console
      console.error(error.stack);
      throw error;
    }
  }

  const handleClick = useCallback(
    async (e: React.MouseEvent<HTMLElement>) => {
      // if no onClick handler or we are not on the client, allow normal href behavior
      if (!onPress || (!process.env.JS_ENV_CLIENT && process.env.NODE_ENV !== 'test')) return;
      // prevent default behavior of navigating to href
      e.preventDefault();
      // await the onClick handler before navigating to href
      try {
        await onPress({ webEvent: e });
      } finally {
        const windowFeatures = rel ? REL_TO_WINDOW_FEATURES[rel] : undefined;
        if (href !== '') window.open(href, target, windowFeatures);
      }
    },
    [onPress, href, target, rel]
  );

  const styledIcon = icon && (
    <Box
      webAs={LinkIcon}
      flexShrink="0"
      height={IconSizeMap[size]}
      width={IconSizeMap[size]}
      {...(iconAlignment === 'left' ? ({ mr: '2x' } as const) : ({ ml: '2x' } as const))}
      // @ts-expect-error NOTE(Ryan) This is an odd usage of Box and fixing it isn't worth it right now
      icon={icon}
    />
  );

  const content = (
    <>
      {iconAlignment === 'left' && styledIcon}
      {children}
      {iconAlignment === 'right' && styledIcon}
    </>
  );

  const typog = getIsTextSized(size) ? (
    <Text
      {...getTypographyComponentProps({ fontWeight, variant, underline, asInlineLink })}
      size={size}
      as={as || 'span'}
      data-testid="kc-link-text"
      display={asInlineLink ? 'inline' : undefined}
    >
      {content}
    </Text>
  ) : (
    <Heading
      {...getTypographyComponentProps({ fontWeight, variant, underline, asInlineLink })}
      size={size}
      webProps={{ as: as || 'div' }}
      data-testid="kc-link-text"
    >
      {content}
    </Heading>
  );

  return (
    <Flex
      webAs="a"
      webAsProps={{ target, href, rel }}
      data-testid={testID}
      display={asInlineLink ? 'inline' : 'inline-flex'}
      sx={{ textDecoration: 'none !important', verticalAlign: 'baseline', ...sx }}
      onPress={(e) => handleClick(e.webEvent!)}
      {...other}
      {...otherWebProps}
    >
      {typog}
    </Flex>
  );
}

export default Link;
